export type OnboardingState =
  | { status: "pending" }
  | { status: "completed"; onboardedAt: string }
  | { status: "incomplete" }
  | { status: "unavailable" };

export const shouldRedirectToOnboarding = (
  authStatus: "loading" | "signed-in" | "signed-out",
  onboarding: OnboardingState,
): boolean => authStatus === "signed-in" && onboarding.status === "incomplete";

// A newer refresh or session reset owns the result, even if an older request
// finishes last. Reads never save or seed writing preferences.
export const createOnboardingReader = (
  read: () => Promise<{ onboardedAt: string | null }>,
  publish: (state: OnboardingState) => void,
) => {
  let revision = 0;

  const reset = () => {
    revision += 1;
    publish({ status: "pending" });
  };

  const refresh = async () => {
    const current = ++revision;
    publish({ status: "pending" });
    let state: OnboardingState;
    try {
      const result = await read();
      if (result.onboardedAt === null) {
        state = { status: "incomplete" };
      } else if (typeof result.onboardedAt === "string" && result.onboardedAt.length > 0) {
        state = { status: "completed", onboardedAt: result.onboardedAt };
      } else {
        state = { status: "unavailable" };
      }
    } catch {
      state = { status: "unavailable" };
    }
    if (current === revision) publish(state);
  };

  return { refresh, reset };
};
